import { gql } from '@apollo/client';
import { getApolloClient } from '@faustwp/core';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { SEO, SchemaMarkup } from '../components';
import ArchivePartnereTemplate from '../wp-templates/archive-partnere';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { MediaBoosterSettingsFragment } from '../fragments/MediaBoosterSettings';

import { PartnereArchiveSEOFragment } from '../fragments/partnere/PartnereArchiveSEOFragment';
import { NavigationMenu } from '../components';
import * as MENUS from '../constants/menus';
import { createSchema, createWebPageSchema, createBreadcrumbSchema } from '../lib/schema';
import { buildSiteUrl } from '../lib/polylang';

const GET_PARTNERE_ARCHIVE = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${PartnereArchiveSEOFragment}
  query GetPartnereArchive(
    $headerLocation: MenuLocationEnum,
    $footerLocation: MenuLocationEnum
  ) {
    partnere(first: 100, where: { language: NB }) {
      nodes {
        id
        title
        slug
        uri
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        partnere {
          layout {
            ... on PartnereLayoutEntryHeaderLayout {
              description
              header
            }
          }
          logoLightmodde {
            node {
              mediaItemUrl
              altText
            }
          }
        }
      }
    }
    contentType(id: "partnere", idType: NAME) {
      ...PartnereArchiveSEOFragment
    }
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`;

export default function PartnereArchive(props) {
  const router = useRouter();
  
  // Extract data from props
  const { data } = { data: props.data || {} };
  const partnere = data?.partnere?.nodes ?? [];
  const archiveSEO = data?.contentType?.seo;
  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const lightLogo = data?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;

  // SEO values
  const pageTitle = archiveSEO?.title || 'Våre samarbeidspartnere';
  const pageDescription = archiveSEO?.description || 'For at våre kunder skal få de beste og tryggeste løsningene, samarbeider vi kun med markedsledende leverandører.';
  const siteName = siteTitle || 'Mediabooster';
  const finalTitle = pageTitle?.includes(siteName) ? pageTitle : `${pageTitle} - ${siteName}`;
  
  // Get social image from WordPress SEO or use fallback
  const socialImage = archiveSEO?.openGraph?.image?.url || 
                     archiveSEO?.openGraph?.image?.secureUrl || 
                     'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/partnere-mediabooster.png';

  const faviconUrl = data?.generalSettings?.favicon?.node?.mediaItemUrl || 
                     data?.mediaboosterSettings?.mediaboosterSettingsFields?.favicon?.node?.mediaItemUrl;

  const canonicalUrl = `${process.env.NEXT_PUBLIC_SITE_URL || 'https://mediabooster.no'}/partnere`;

  // Generate schema for partners archive page
  const pageSchema = useMemo(() => {
    const breadcrumbs = [
      { label: 'Hjem', path: '/' },
      { label: 'Partnere', path: '/partnere' }
    ];

    // Create ItemList schema for partners
    const partnerItems = (partnere || []).map((partner, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': partner.title,
      'url': `https://mediabooster.no${partner.uri}`
    }));

    return createSchema([
      createWebPageSchema({
        title: finalTitle,
        description: pageDescription,
        url: '/partnere'
      }),
      {
        '@type': 'CollectionPage',
        '@id': 'https://mediabooster.no/partnere/#collectionpage',
        'name': finalTitle,
        'description': pageDescription,
        'url': 'https://mediabooster.no/partnere',
        'mainEntity': {
          '@type': 'ItemList',
          'itemListElement': partnerItems,
          'numberOfItems': partnerItems.length
        }
      },
      createBreadcrumbSchema(breadcrumbs)
    ]);
  }, [finalTitle, pageDescription, partnere]);

  return (
    <>
      <SEO
        title={finalTitle}
        description={pageDescription}
        imageUrl={socialImage}
        url={canonicalUrl}
        faviconUrl={faviconUrl}
        siteName={siteName}
        ogType="website"
        locale="nb_NO"
        hreflangAlternates={[
          { hrefLang: 'no', href: canonicalUrl },
          { hrefLang: 'en', href: buildSiteUrl('/en/partners') },
          { hrefLang: 'x-default', href: canonicalUrl },
        ]}
      />
      <SchemaMarkup schema={pageSchema} />
      <ArchivePartnereTemplate {...props} />
    </>
  );
}

export async function getStaticProps() {
  const apolloClient = getApolloClient();
  
  try {
    // Fetch page-specific data
    const { data: pageData } = await apolloClient.query({
      query: GET_PARTNERE_ARCHIVE,
      variables: {
        headerLocation: MENUS.PRIMARY_LOCATION,
        footerLocation: MENUS.FOOTER_LOCATION,
      },
      fetchPolicy: 'no-cache'
    });
    
    // Fetch shared data using exact same queries as components
    const { data: sharedData } = await apolloClient.query({
      query: gql`
        query GetSharedPartnereData {
          # Footer data - exact same query as Footer component
          tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
            nodes {
              id
              title
              slug
              uri
              terms {
                nodes {
                  id
                  name
                }
              }
              tjenesterPost {
                serviceDescription
              }
            }
          }
          
          # Partners data for NavigationMenu (Norwegian only on /partnere)
          partnere(first: 100, where: { language: NB }) {
            nodes {
              id
              title
              slug
              uri
            }
          }
          
          # MediaBooster settings - includes Footer data
          mediaboosterSettings {
            mediaboosterSettingsFields {
              logo {
                node {
                  mediaItemUrl
                  altText
                }
              }
              logoIcon {
                node {
                  mediaItemUrl
                  altText
                }
              }
              nyhetsbrev {
                tittelNyhetsbrev
                innholdNyhetsbrev
              }
            }
          }
          
          # Frontpage tjenesteboks data used by Header
          page(id: "14", idType: DATABASE_ID) {
            frontPage {
              tjenesteboks {
                tjenestenavn
                underoverskrift
                link
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache'
    });
    
    return {
      props: {
        data: pageData,
        sharedData: sharedData
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching partnere archive data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
} 